import axios from "axios";
import toast from "react-hot-toast";
import {
  API_BASE_URL,
  DEFAULT_HEADERS,
  REQUEST_TIMEOUT,
  ERROR_MESSAGES,
} from "./config";
import { getCsrfHeaders, applyCsrfFromResponse, recoverCsrfToken } from "./csrf";
import { hasSessionHint } from "../utils/authRouteUtils";
import {
  dispatchSessionExpired,
  refreshSessionWithQueue,
} from "./sessionRefresh";

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: REQUEST_TIMEOUT,
  headers: DEFAULT_HEADERS,
  withCredentials: true,
});

export const UPLOAD_TIMEOUT = 120000;

let lastRateLimitToastTimestamp = 0;

const isAuthRoute = (requestUrl = '') =>
  requestUrl.includes('/auth/login') ||
  requestUrl.includes('/auth/refresh') ||
  requestUrl.includes('/auth/verify-') ||
  requestUrl.includes('/auth/forgot-password') ||
  requestUrl.includes('/auth/reset-password');

const applyCsrfToRequestHeaders = (headers) => {
  const csrfHeaders = getCsrfHeaders();
  const csrfToken = csrfHeaders['X-CSRF-Token'];
  if (!csrfToken) return headers;

  if (typeof headers.set === 'function') {
    headers.set('X-CSRF-Token', csrfToken);
  } else {
    Object.keys(headers).forEach((key) => {
      if (key.toLowerCase() === 'x-csrf-token') {
        delete headers[key];
      }
    });
    headers['X-CSRF-Token'] = csrfToken;
  }
  return headers;
};

const isCsrfErrorPayload = (data) =>
  data?.responseMessage === 'Invalid CSRF token' ||
  data?.message === 'Invalid CSRF token' ||
  (typeof data?.responseMessage === 'string' && data.responseMessage.toLowerCase().includes('csrf')) ||
  (typeof data?.message === 'string' && data.message.toLowerCase().includes('csrf'));

const isCsrfError = (status, data) =>
  status === 403 && isCsrfErrorPayload(data);

apiClient.interceptors.request.use(
  (config) => {
    config.withCredentials = true;
    config.headers = applyCsrfToRequestHeaders(config.headers || {});

    const isFormData = config.data instanceof FormData ||
      config.data?.constructor?.name === 'FormData' ||
      (config.data && typeof config.data === 'object' && config.data.entries);

    if (isFormData) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => {
    applyCsrfFromResponse(response.data?.data);

    if (response.data instanceof Blob || (response.data && typeof response.data === 'object' && response.data.constructor && response.data.constructor.name === 'Blob')) {
      return response.data;
    }

    if (response.data === null && response.status === 200) {
      return {
        responseCode: response.status,
        responseMessage: "Operation completed successfully",
        data: null,
      };
    }

    return response.data;
  },
  async (error) => {
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    const originalRequest = error.config || {};

    if (error.response) {
      const { status, data } = error.response;

      const getBackendMessage = (fallbackKey) => {
        if (!data || typeof data !== 'object') return ERROR_MESSAGES[fallbackKey] || ERROR_MESSAGES.SERVER_ERROR;
        return (
          data.message ||
          data.responseMessage ||
          data.error ||
          ERROR_MESSAGES[fallbackKey] ||
          ERROR_MESSAGES.SERVER_ERROR
        );
      };

      if (data instanceof Blob) {
        try {
          const errorText = await data.text();
          const errorData = JSON.parse(errorText);
          data = errorData;
          error.response.data = errorData;
        } catch {
          // Keep data as Blob if not JSON
        }
      }

      if (isCsrfError(status, data) && !originalRequest._csrfRetry) {
        const requestUrl = originalRequest.url || '';

        if (!isAuthRoute(requestUrl) && !requestUrl.includes('/auth/me')) {
          originalRequest._csrfRetry = true;

          try {
            const recovered = await recoverCsrfToken();
            if (recovered) {
              return apiClient(originalRequest);
            }
          } catch {
            // fall through to normal 403 handling
          }
        }
      }

      if (status === 401 && !originalRequest._retry) {
        const requestUrl = originalRequest.url || '';

        if (!isAuthRoute(requestUrl) && !hasSessionHint()) {
          throw new Error(getBackendMessage("UNAUTHORIZED") || "Not authenticated.");
        }

        if (!isAuthRoute(requestUrl)) {
          originalRequest._retry = true;

          try {
            await refreshSessionWithQueue();
            return apiClient(originalRequest);
          } catch (refreshError) {
            const isAuthErrorResponse = Boolean(refreshError?.response) ||
              refreshError?.message?.includes("Session expired") ||
              refreshError?.message?.includes("UNAUTHORIZED");

            if (isAuthErrorResponse) {
              dispatchSessionExpired();
            }
            throw new Error(getBackendMessage("UNAUTHORIZED") || "Session expired. Please login again.");
          }
        }
      }

      switch (status) {
        case 400: {
          const validationError = new Error(getBackendMessage("BAD_REQUEST"));
          validationError.responseData = data;
          if (data && data.errors) {
            validationError.errors = data.errors;
          }
          validationError.response = error.response;
          throw validationError;
        }
        case 401: {
          const requestUrl = originalRequest.url || '';
          if (!isAuthRoute(requestUrl) && originalRequest._retry) {
            dispatchSessionExpired();
          }
          throw new Error(getBackendMessage("UNAUTHORIZED") || "Session expired. Please login again.");
        }
        case 403:
          throw new Error(getBackendMessage("FORBIDDEN"));
        case 404:
          throw new Error(getBackendMessage("NOT_FOUND"));
        case 409: {
          const conflictError = new Error(getBackendMessage("BAD_REQUEST"));
          conflictError.responseData = data;
          conflictError.response = error.response;
          throw conflictError;
        }
        case 422:
          throw new Error(getBackendMessage("VALIDATION_ERROR"));
        case 429: {
          const rateLimitMessage =
            data?.responseMessage ||
            data?.message ||
            "Too many requests. Please wait a moment and try again.";

          const now = Date.now();
          if (now - lastRateLimitToastTimestamp > 5000) {
            lastRateLimitToastTimestamp = now;
            toast.error(rateLimitMessage, {
              id: "rate-limit-exceeded",
              duration: 5000,
            });
          }

          const rateLimitError = new Error(rateLimitMessage);
          rateLimitError.responseData = data;
          rateLimitError.response = error.response;
          rateLimitError.isRateLimited = true;
          throw rateLimitError;
        }
        case 500:
          throw new Error(getBackendMessage("SERVER_ERROR"));
        default:
          throw new Error(getBackendMessage("SERVER_ERROR"));
      }
    } else if (error.request) {
      throw new Error(ERROR_MESSAGES.NETWORK_ERROR);
    } else {
      throw new Error(error.message || ERROR_MESSAGES.UNKNOWN_ERROR);
    }
  }
);

export const get = (url, config = {}) => apiClient.get(url, config);
export const post = (url, data = {}, config = {}) =>
  apiClient.post(url, data, config);
export const put = (url, data = {}, config = {}) =>
  apiClient.put(url, data, config);
export const patch = (url, data = {}, config = {}) =>
  apiClient.patch(url, data, config);
export const del = (url, config = {}) => apiClient.delete(url, config);

export default apiClient;
